import { REALTIME_WS_PATH } from '../Helpers/realtimeConstants'
import { parseRealtimeEvent } from '../Helpers/realtimeEventGuard'
import type { RealtimeEvent } from '../Types/events'

const RECONNECT_DELAY_MS = 2000

type RealtimeEventListener = (event: RealtimeEvent) => void | Promise<void>

const listeners = new Set<RealtimeEventListener>()

let websocket: WebSocket | null = null
let reconnectTimeout: ReturnType<typeof setTimeout> | null = null
let shouldReconnect = false
let isConnecting = false
let isPageSuspended = false
let arePageLifecycleListenersRegistered = false

function getRealtimeUrl() {
    const configuredUrl = import.meta.env.VITE_REALTIME_WS_URL

    if (configuredUrl) {
        return configuredUrl
    }

    const url = new URL(window.location.origin)
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'

    if (import.meta.env.DEV) {
        url.port = import.meta.env.VITE_REALTIME_WS_DEV_PORT ?? '3001'
    }

    url.pathname = REALTIME_WS_PATH
    url.search = ''
    url.hash = ''

    return url.toString()
}

function clearReconnectTimeout() {
    if (!reconnectTimeout) {
        return
    }

    clearTimeout(reconnectTimeout)
    reconnectTimeout = null
}

function scheduleReconnect() {
    if (!shouldReconnect || listeners.size === 0 || isPageSuspended) {
        return
    }

    clearReconnectTimeout()
    reconnectTimeout = setTimeout(function () {
        void connect()
    }, RECONNECT_DELAY_MS)
}

function dispatchRealtimeEvent(event: RealtimeEvent) {
    for (const listener of listeners) {
        void Promise.resolve(listener(event)).catch((error) => {
            console.error('Realtime event listener failed', error)
        })
    }
}

function closeCurrentWebSocket() {
    clearReconnectTimeout()

    const currentWebsocket = websocket
    websocket = null

    if (currentWebsocket && currentWebsocket.readyState !== WebSocket.CLOSED) {
        currentWebsocket.close()
    }
}

function setupPageLifecycleListeners() {
    if (arePageLifecycleListenersRegistered || typeof window === 'undefined') {
        return
    }

    arePageLifecycleListenersRegistered = true

    window.addEventListener('pagehide', function () {
        isPageSuspended = true
        closeCurrentWebSocket()
    })

    window.addEventListener('pageshow', function () {
        isPageSuspended = false

        if (shouldReconnect && listeners.size > 0) {
            void connect()
        }
    })
}

async function connect() {
    if (isConnecting || websocket || listeners.size === 0 || isPageSuspended) {
        return
    }

    isConnecting = true

    try {
        if (!shouldReconnect || listeners.size === 0) {
            return
        }

        const socket = new WebSocket(getRealtimeUrl())
        websocket = socket

        socket.onmessage = function (event) {
            if (typeof event.data !== 'string') {
                return
            }

            const realtimeEvent = parseRealtimeEvent(event.data)

            if (!realtimeEvent) {
                return
            }

            dispatchRealtimeEvent(realtimeEvent)
        }

        socket.onerror = function () {
            socket.close()
        }

        socket.onclose = function () {
            if (websocket === socket) {
                websocket = null
            }

            scheduleReconnect()
        }
    } catch {
        scheduleReconnect()
    } finally {
        isConnecting = false
    }
}

export function subscribeToRealtimeEvents(listener: RealtimeEventListener) {
    setupPageLifecycleListeners()

    listeners.add(listener)
    shouldReconnect = true
    clearReconnectTimeout()
    void connect()

    return function unsubscribeFromRealtimeEvents() {
        listeners.delete(listener)

        if (listeners.size > 0) {
            return
        }

        shouldReconnect = false
        closeCurrentWebSocket()
    }
}

export function reconnectRealtimeEvents() {
    if (isPageSuspended) {
        return
    }

    if (websocket) {
        websocket.close()
        return
    }

    void connect()
}
